// src/modules/PeriodicReporting/utils/fiExport.js
// Template-driven export engine for Periodic Reporting.
//
// This file implements the export logic. It is format-agnostic:
// adding a new export format requires only changes to config/reportTemplates.js.

import { getTemplate, DEFAULT_TEMPLATE_ID } from '../config/reportTemplates';

/**
 * Build a templated export payload from a report.
 *
 * The export format is fully driven by the template configuration:
 *   - metaFieldMappings defines which fields appear in reportMeta
 *   - excludeFromData controls what is omitted from the data section
 *   - dynRowKeys lists dynamic row tables to include
 *   - exportStructure.topLevelKey names the field container in the output
 *
 * @param {Object} report - Report object from PeriodicReportingService
 * @param {string} [templateId] - Template ID from config/reportTemplates.js
 * @returns {Object} Export payload structured per the template
 * @throws {Error} If templateId is not registered in REPORT_TEMPLATES
 */
export function buildTemplatedExport(report, templateId = DEFAULT_TEMPLATE_ID) {
  const template = getTemplate(templateId);
  if (!template) {
    throw new Error(
      `Unknown export template: "${templateId}". Register it in config/reportTemplates.js`
    );
  }

  const fields = report.fields || {};
  const excludeKeys = new Set(template.excludeFromData || []);
  const dynRowKeySet = new Set(template.dynRowKeys || []);

  // --- reportMeta: static report properties + mapped meta fields ---
  const reportingMeta = {};
  for (const [exportKey, internalKey] of Object.entries(template.metaFieldMappings || {})) {
    const value = fields[internalKey];
    reportingMeta[exportKey] =
      value !== undefined && value !== '' && value !== null ? value : null;
  }

  // --- data: all non-excluded, non-dynamic-row fields with non-empty values ---
  const topLevelKey = template.exportStructure?.topLevelKey || 'data';
  const data = {};
  for (const [key, value] of Object.entries(fields)) {
    if (excludeKeys.has(key)) continue;
    if (dynRowKeySet.has(key)) continue;
    const isEmpty =
      value === undefined ||
      value === '' ||
      value === null ||
      (Array.isArray(value) && value.length === 0);
    if (!isEmpty) {
      data[key] = value;
    }
  }

  // --- dynRows: dynamic row tables, only if template includes them ---
  const dynRows = {};
  if (template.exportStructure?.dynRowsIncluded) {
    for (const dynKey of template.dynRowKeys || []) {
      if (Array.isArray(fields[dynKey]) && fields[dynKey].length > 0) {
        dynRows[dynKey] = fields[dynKey];
      }
    }
  }

  return {
    exportMeta: {
      exportedAt: new Date().toISOString(),
      system: 'Prime RMS',
      templateId: template.id,
      templateVersion: template.templateVersion,
      schemaRef: template.schemaRef,
    },
    reportMeta: {
      id: report.id,
      year: report.year,
      status: report.status,
      createdAt: report.createdAt,
      updatedAt: report.updatedAt,
      createdBy: report.createdBy,
      statusUpdatedAt: report.statusUpdatedAt || null,
      statusUpdatedBy: report.statusUpdatedBy || null,
      ...reportingMeta,
    },
    [topLevelKey]: data,
    ...dynRows,
  };
}

/**
 * Validate that a report has all specified required fields filled.
 * Returns which fields are missing so the caller can surface them to the user.
 *
 * @param {Object} report - Report from PeriodicReportingService
 * @param {string[]} [requiredFieldIds] - Field IDs that must be non-empty
 * @returns {{ valid: boolean, missingFields: string[] }}
 */
export function validateExportData(report, requiredFieldIds = []) {
  const fields = report.fields || {};
  const missingFields = requiredFieldIds.filter((id) => {
    const val = fields[id];
    return (
      val === undefined ||
      val === '' ||
      val === null ||
      (Array.isArray(val) && val.length === 0)
    );
  });
  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}
